import { createContext, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { DICT } from "@/data/i18n";

export const ease = [0.22, 1, 0.36, 1];

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: DICT.en });

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("ve_lang") || "en");
  useEffect(() => {
    localStorage.setItem("ve_lang", lang);
    document.documentElement.lang = lang;
    document.title = DICT[lang].metaTitle;
  }, [lang]);
  return <LangCtx.Provider value={{ lang, setLang, t: DICT[lang] }}>{children}</LangCtx.Provider>;
};

export const useLang = () => useContext(LangCtx);

export const Segs = ({ segs }) =>
  segs.map(([txt, style], i) =>
    style === 1 ? <span key={i} className="text-[#00A651]">{txt}</span>
    : style === 2 ? <span key={i} className="text-[#D97D00]">{txt}</span>
    : <span key={i}>{txt}</span>
  );

export const Reveal = ({ children, delay = 0, y = 28, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, delay, ease }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const SectionHead = ({ num, kicker, segs, desc, dark = false, testid }) => (
  <div className="grid gap-8 lg:grid-cols-12 lg:items-end mb-14 lg:mb-20" data-testid={testid}>
    <Reveal className="lg:col-span-7">
      <p className={`font-mono2 text-xs tracking-[0.25em] uppercase mb-5 ${dark ? "text-[#F39C12]" : "text-[#00A651]"}`}>
        {num} — {kicker}
      </p>
      <h2 className={`font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight ${dark ? "text-white" : "text-[#0F295B]"}`}>
        <Segs segs={segs} />
      </h2>
    </Reveal>
    {desc && (
      <Reveal delay={0.15} className={`lg:col-span-5 text-base md:text-lg leading-relaxed ${dark ? "text-slate-300" : "text-[#4A5B78]"}`}>
        {desc}
      </Reveal>
    )}
  </div>
);

export const WaIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.21 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.29.17-1.41-.07-.12-.27-.2-.57-.35zM12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.32l-.34-.2-3.56.93.95-3.47-.22-.36a9.42 9.42 0 0 1-1.44-5.02c0-5.2 4.24-9.44 9.45-9.44 2.52 0 4.89.98 6.67 2.77a9.37 9.37 0 0 1 2.76 6.68c0 5.2-4.24 9.43-9.45 9.43zm8.04-17.47A11.3 11.3 0 0 0 12.05.7C5.78.7.68 5.8.68 12.06c0 2 .52 3.96 1.52 5.68L.58 23.3l5.7-1.5a11.33 11.33 0 0 0 5.77 1.47h.01c6.27 0 11.37-5.1 11.37-11.37 0-3.04-1.18-5.9-3.33-8.04z" />
  </svg>
);
